import React, { useCallback, useEffect, useState } from 'react';

import { Stack } from '@mantine/core';
import { Eventcalendar, MbscCalendarEvent } from '@mobiscroll/react';
import dayjs from 'dayjs';
import useDebounce from 'hooks/useDebounce';
import useWorkoutsCalendar from 'hooks/workout/useWorkoutsCalendar';
import type { KiloClassType, WorkoutCalendarKiloClassType, WorkoutCalendarWorkoutSummary } from 'types';

import AgendaHeader from './components/AgendaHeader';
import AgendaItem from './components/AgendaItem';
import { AgendaEvent, IProgramCalendarAgendaViewProps, WorkoutAssignment } from './interface';
import useWorkoutProgrammingCalendarStyles from './styles';

const AGENDA_COLUMN_LABELS = ['Event', 'Workout Assigned'];

function workoutIdToAssignment(workout: WorkoutCalendarWorkoutSummary | null): WorkoutAssignment | null {
  if (!workout) return null;
  return {
    workoutId: workout.workoutId,
    name: workout.name,
    isVisibleToAthletes: workout.isVisibleToAthletes,
    hasResults: workout.hasResults,
    workoutTemplate: { workoutTemplateId: '', name: workout.name },
  };
}

function toKiloClassType(row: WorkoutCalendarKiloClassType): KiloClassType {
  const color = row.color?.trim();
  return {
    kiloClassTypeId: row.kiloClassTypeId,
    name: row.name,
    color,
    gym: '',
    description: null,
    duration: 0,
    capacity: 0,
    waitlistLimit: null,
    location: null,
    deletedAt: null,
    createdAt: '',
    updatedAt: '',
    reservationAndCancellationSettings: {},
  } as KiloClassType;
}

function toAgendaEvent(row: WorkoutCalendarKiloClassType, date: string): MbscCalendarEvent {
  return {
    start: new Date(`${date}T00:00`),
    totalPrograms: row.classCount,
    id: `wc-agenda-${date}-${row.kiloClassTypeId}`,
    programId: '',
    programDate: date,
    workoutNotes: null,
    kiloClassType: toKiloClassType(row),
    programName: row.name,
    createdAt: '',
    updatedAt: '',
    gym: '',
    isProgrammed: !!row.workout,
    workoutAssignment: workoutIdToAssignment(row.workout),
  };
}

/** Agenda when workout tracker is enabled */
const CalendarAgendaView = ({
  programCalendarNavigationButtons,
  date,
  setDate,
  searchFilters,
  calendarMaxDate,
}: IProgramCalendarAgendaViewProps): JSX.Element => {
  const { classes } = useWorkoutProgrammingCalendarStyles();
  const [dateSelected, setDateSelected] = useState<string>(date);
  const debouncedStart = useDebounce(dateSelected, 200);

  useEffect(() => {
    setDateSelected(date);
  }, [date]);

  const {
    data: workoutCalData,
    isSuccess: workoutCalSuccess,
    isError: workoutCalError,
  } = useWorkoutsCalendar({
    startDate: debouncedStart,
    endDate: debouncedStart,
  });

  const onPageLoading = (event: {
    firstDay: string | number | Date | dayjs.Dayjs | null | undefined;
  }): void => {
    const newDate = dayjs(event.firstDay).format('YYYY-MM-DD');
    if (newDate !== dateSelected) {
      setDateSelected(newDate);
      setDate(newDate);
    }
  };

  const handleDateSelect = (selected: Date): void => {
    const newDate = dayjs(selected).format('YYYY-MM-DD');
    setDateSelected(newDate);
    setDate(newDate);
  };

  const renderHeader = useCallback(
    (): JSX.Element =>
      programCalendarNavigationButtons({
        selectedDate: new Date(`${dateSelected}T00:00`),
        onDateSelect: handleDateSelect,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [programCalendarNavigationButtons, dateSelected],
  );

  const day =
    workoutCalSuccess && workoutCalData ? workoutCalData.data.find(d => d.date === debouncedStart) : null;
  const allRows = day?.kiloClassTypes ?? [];
  const rows =
    searchFilters.kiloClassTypes.length > 0
      ? allRows.filter(k => searchFilters.kiloClassTypes.includes(k.kiloClassTypeId))
      : allRows;
  const calendarEvents = rows.map(row => toAgendaEvent(row, debouncedStart));

  const renderAgenda = useCallback(
    (data: any): JSX.Element => {
      if (data.length && data[0].events) {
        const events = data[0].events;
        const agendaItems = events.map((event: { original: AgendaEvent }) => (
          <AgendaItem
            key={event.original.id}
            event={event.original}
            assignment={event.original.workoutAssignment ?? null}
          />
        ));

        return (
          <Stack className={classes.agendaContent}>
            <AgendaHeader
              columnLabels={AGENDA_COLUMN_LABELS}
              gridClassName={`${classes.header} ${classes.grid}`}
            />
            {agendaItems}
          </Stack>
        );
      }
      return <></>;
    },
    [classes],
  );

  if (workoutCalError) return <>Something went wrong</>;

  return (
    <Eventcalendar
      renderHeader={renderHeader}
      view={{ agenda: { type: 'day' } }}
      dateFormatLong='DDD, MMM D'
      selectedDate={new Date(`${dateSelected}T00:00`)}
      theme='ios'
      themeVariant='light'
      max={calendarMaxDate}
      data={calendarEvents}
      className={classes.calendar}
      renderAgenda={renderAgenda}
      onPageLoading={onPageLoading}
    />
  );
};

export default CalendarAgendaView;
